import React, { useMemo } from 'react';
import { Box, Typography, styled, useTheme } from '@mui/material';
import CustomTooltip from './CustomTooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkflowIcon from '@mui/icons-material/AccountTree';
import ModeIcon from '@mui/icons-material/Category';
import StyleIcon from '@mui/icons-material/Palette';
import UploadIcon from '@mui/icons-material/CloudUpload';
import PromptIcon from '@mui/icons-material/Create';
import GenerateIcon from '@mui/icons-material/AutoFixHigh';

const StageContainer = styled(Box)(({ theme, isActive, completed }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '5px 10px',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',
  backgroundColor: isActive 
    ? theme.palette.mode === 'dark'
      ? 'rgba(77, 216, 216, 0.15)'
      : 'rgba(77, 216, 216, 0.1)'
    : 'transparent',
  opacity: completed ? 0.85 : 1,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(77, 216, 216, 0.2)'
      : 'rgba(77, 216, 216, 0.15)',
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '3px',
    minWidth: 'auto',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '8px',
    animation: isActive ? 'pulse 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
    pointerEvents: 'none',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(77, 216, 216, 0.6)',
    },
    '50%': {
      boxShadow: '0 0 0 12px rgba(77, 216, 216, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(77, 216, 216, 0)',
    },
  },
}));

const StageIcon = styled(Box)(({ theme, completed, isActive }) => ({
  marginRight: '12px',
  color: completed ? '#4CAF50' : theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  transform: isActive ? 'scale(1.1)' : 'scale(1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: isActive ? 'iconPulse 2s infinite' : 'none',
  filter: isActive ? `drop-shadow(0 0 8px ${theme.palette.primary.main}40)` : 'none',
  [theme.breakpoints.down('sm')]: {
    marginRight: 0,
    marginBottom: '1px',
    transform: 'scale(0.7)',
  },
  '@keyframes iconPulse': {
    '0%': { transform: 'scale(1.1)' },
    '50%': { transform: 'scale(1.2)' },
    '100%': { transform: 'scale(1.1)' }
  }
}));

const ProgressLine = styled(Box)(({ theme, isActive, completed }) => ({
  position: 'absolute',
  top: '50%',
  right: '-8px',
  height: '2px',
  width: '16px',
  background: completed 
    ? '#4CAF50'
    : isActive 
      ? theme.palette.primary.main
      : theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-1px',
    right: '-1px',
    bottom: 0,
    background: completed 
      ? '#4CAF50'
      : isActive 
        ? theme.palette.primary.main
        : 'transparent',
    transform: 'scaleY(0)',
    transformOrigin: 'top',
    animation: isActive ? 'growLine 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards' : 'none',
  },
  '@keyframes growLine': {
    '0%': { transform: 'scaleY(0)' },
    '50%': { transform: 'scaleY(0.5)' },
    '100%': { transform: 'scaleY(1)' }
  }
}));

export const stages = [
  {
    id: 'workflow',
    label: 'İş Akışı Seç',
    icon: WorkflowIcon,
    tooltip: 'Devam etmek için bir iş akışı seçin'
  },
  {
    id: 'mode',
    label: 'Mod Seçimi',
    icon: ModeIcon,
    tooltip: 'Projenize en uygun modu seçin'
  },
  {
    id: 'style',
    label: 'Stil Seçimi',
    icon: StyleIcon,
    tooltip: 'Tasarımınız için bir stil seçin'
  },
  {
    id: 'upload',
    label: 'Fotoğraf/Maske Ekle',
    icon: UploadIcon,
    tooltip: 'Tasarımınızı yükleyin veya devam etmek için atlayın'
  },
  {
    id: 'prompt',
    label: 'Komut Girin',
    icon: PromptIcon,
    tooltip: 'Çıktınızı özelleştirmek için detaylı bir açıklama yazın'
  },
  {
    id: 'generate',
    label: 'Çıktı Oluştur',
    icon: GenerateIcon,
    tooltip: 'Tasarımınızı gözden geçirin ve sonlandırın'
  }
];

const WorkflowTracker = ({ currentStage, completedStages = [], onStageClick, workflowId }) => {
  const theme = useTheme();
  
  // Filter stages based on workflow type
  const filteredStages = useMemo(() => {
    if (workflowId === 'furniture-removal') {
      // For furniture removal, skip the style and prompt stages
      return stages.filter(stage => stage.id !== 'style' && stage.id !== 'prompt');
    } else if (workflowId === 'staging') {
      // For staging workflow, keep all stages but customize them
      return stages.map(stage => {
        if (stage.id === 'style') {
          return {
            ...stage,
            label: 'Referans Seçimi', // Change label for style stage
            tooltip: 'Referans olarak kullanılacak bir görsel seçin'
          };
        }
        return stage;
      });
    }
    return stages;
  }, [workflowId]);
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(18, 18, 23, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)',
        padding: { xs: '5px 8px', sm: '5px 14px' },
        borderRadius: '12px',
        backdropFilter: 'blur(10px)',
        boxShadow: theme => `0 8px 32px ${theme.palette.primary.main}20`,
        border: theme => `1px solid ${theme.palette.primary.main}20`,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme => `0 12px 40px ${theme.palette.primary.main}30`,
        },
        position: 'relative',
        width: '100%',
        maxWidth: '800px',
        margin: '0 auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          background: theme => `
            radial-gradient(circle at center, ${theme.palette.primary.main}15 0%, transparent 70%),
            repeating-linear-gradient(45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 10px),
            repeating-linear-gradient(-45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 10px)
          `,
          backgroundSize: '100% 100%, 20px 20px, 20px 20px',
          opacity: 0.5,
          zIndex: -1,
        },
      }}
    >
      {filteredStages.map((stage, index) => (
        <Box key={stage.id} sx={{ position: 'relative' }}>
          <CustomTooltip 
            title={stage.tooltip} 
            placement="top"
          >
            <StageContainer
              isActive={currentStage === stage.id}
              completed={completedStages.includes(stage.id)}
              onClick={() => onStageClick(stage.id)}
            >
              <StageIcon 
                completed={completedStages.includes(stage.id)}
                isActive={currentStage === stage.id}
              >
                {completedStages.includes(stage.id) ? (
                  <CheckCircleIcon />
                ) : (
                  <stage.icon />
                )}
              </StageIcon>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: currentStage === stage.id ? 600 : 500,
                  color: currentStage === stage.id 
                    ? theme.palette.primary.main
                    : theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.9)'
                      : 'rgba(0, 0, 0, 0.8)',
                  display: { xs: 'none', sm: 'block' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  letterSpacing: '0.01em',
                  transition: 'all 0.3s ease',
                }}
              >
                {stage.label}
              </Typography>
            </StageContainer>
          </CustomTooltip>
          {index < filteredStages.length - 1 && (
            <ProgressLine
              isActive={currentStage === stage.id}
              completed={completedStages.includes(stage.id)}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default WorkflowTracker;
